import React, { useEffect, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useHistory } from 'react-router-dom';
import useUser from 'state/user';
import useTherapist from 'state/therapists';
import useAppointments from 'state/appointments';
import useCategories from 'state/categories';
import RatingStars from 'components/RatingStars';
import {
    BRAND_GREEN_PRIMARY,
    DARKER_TEXT,
    DARK_TEXT,
    PLACEHOLDER_TEXT,
} from 'resources/constants/colors';
import { IMAGES_URL } from 'resources/constants/urls';
import NoProfileSVG from 'resources/img/no-pic-therapist.png';
import AppointmentCard from 'pages/Home/pages/Calendar/components/AppointmentCard';
import SubpageTopBar from '../../components/SubpageTopBar';

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------
const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
`;

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------
const PageWrap = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100%;
    padding-bottom: 90px;
    animation: ${fadeUp} 0.3s ease both;
`;

const Content = styled.div`
    padding: 0 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

// ---------------------------------------------------------------------------
// Profile card
// ---------------------------------------------------------------------------
const ProfileCard = styled.div`
    background: #fff;
    border-radius: 20px;
    box-shadow: 0 2px 14px rgba(0,0,0,0.08);
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const ProfileHeader = styled.div`
    display: flex;
    gap: 16px;
    align-items: center;
`;

const Avatar = styled.div`
    width: 72px;
    height: 72px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    background: #eee;
    border: 2.5px solid ${BRAND_GREEN_PRIMARY}50;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const ProfileInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const TherapistName = styled.h2`
    font-size: 17px;
    font-weight: 800;
    color: ${DARKER_TEXT};
    margin: 0;
    letter-spacing: -0.2px;
`;

const TherapistTitle = styled.p`
    font-size: 12px;
    color: ${PLACEHOLDER_TEXT};
    margin: 0;
`;

const Phrase = styled.p`
    font-size: 13px;
    font-style: italic;
    color: ${BRAND_GREEN_PRIMARY};
    margin: 0;
    line-height: 1.5;
    border-left: 3px solid ${BRAND_GREEN_PRIMARY}40;
    padding-left: 10px;
`;

const SectionLabel = styled.p`
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: #aaa;
    margin: 0 0 6px;
`;

const RatingRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const AboutText = styled.p`
    font-size: 14px;
    color: ${DARK_TEXT};
    margin: 0;
    line-height: 1.6;
`;

// ---------------------------------------------------------------------------
// Availability grid
// ---------------------------------------------------------------------------
const AvailabilityCard = styled.div`
    background: #fff;
    border-radius: 20px;
    box-shadow: 0 2px 14px rgba(0,0,0,0.08);
    padding: 20px;
`;

const DayGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 4px;
`;

const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

const DayChip = styled.div`
    background: ${BRAND_GREEN_PRIMARY}15;
    color: ${BRAND_GREEN_PRIMARY};
    border-radius: 20px;
    padding: 5px 12px;
    font-size: 12px;
    font-weight: 600;
`;

// ---------------------------------------------------------------------------
// Specialties card
// ---------------------------------------------------------------------------
const SpecialtiesCard = styled.div`
    background: #fff;
    border-radius: 20px;
    box-shadow: 0 2px 14px rgba(0,0,0,0.08);
    padding: 20px;
`;

const ChipRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 6px;
`;

const SpecialtyChip = styled.span`
    background: ${BRAND_GREEN_PRIMARY}15;
    color: ${BRAND_GREEN_PRIMARY};
    border-radius: 20px;
    padding: 5px 14px;
    font-size: 12px;
    font-weight: 600;
`;

// ---------------------------------------------------------------------------
// Reviews card
// ---------------------------------------------------------------------------
const ReviewsCard = styled.div`
    background: #fff;
    border-radius: 20px;
    box-shadow: 0 2px 14px rgba(0,0,0,0.08);
    padding: 20px;
`;

const ReviewItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child { border-bottom: none; padding-bottom: 0; }
    &:first-child { padding-top: 0; }
`;

const ReviewAuthor = styled.p`
    font-size: 12px;
    font-weight: 700;
    color: ${DARKER_TEXT};
    margin: 0;
`;

const ReviewComment = styled.p`
    font-size: 13px;
    color: ${DARK_TEXT};
    margin: 0;
    line-height: 1.5;
`;

const ReviewStarRow = styled.div`
    display: flex;
    gap: 2px;
    margin-bottom: 2px;
`;

const StarMini = styled.span`
    font-size: 12px;
    color: ${({ filled }) => (filled ? '#F5A623' : '#ddd')};
`;

// ---------------------------------------------------------------------------
// CTA buttons
// ---------------------------------------------------------------------------
const CtaRow = styled.div`
    display: flex;
    gap: 12px;
`;

const BookButton = styled.button`
    flex: 1;
    background: ${BRAND_GREEN_PRIMARY};
    color: #fff;
    border: none;
    border-radius: 14px;
    padding: 14px 20px;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    box-shadow: 0 4px 14px ${BRAND_GREEN_PRIMARY}40;
    transition: opacity 0.15s;

    &:active { opacity: 0.85; }
`;

const MessageButton = styled.button`
    flex: 1;
    background: #fff;
    color: ${BRAND_GREEN_PRIMARY};
    border: 2px solid ${BRAND_GREEN_PRIMARY};
    border-radius: 14px;
    padding: 14px 20px;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: opacity 0.15s;

    &:active { opacity: 0.85; }
`;

// ---------------------------------------------------------------------------
// History section
// ---------------------------------------------------------------------------
const HistoryCard = styled.div`
    background: #fff;
    border-radius: 20px;
    box-shadow: 0 2px 14px rgba(0,0,0,0.08);
    padding: 20px;
`;

const EmptyHistoryText = styled.p`
    font-size: 13px;
    color: ${PLACEHOLDER_TEXT};
    text-align: center;
    margin: 16px 0 0;
`;

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------
const SkeletonBlock = styled.div`
    background: #f0f0f0;
    border-radius: 16px;
    height: ${({ h }) => h || '16px'};
    width: ${({ w }) => w || '100%'};
    margin-bottom: ${({ mb }) => mb || '0'};
`;

const SkeletonCard = styled.div`
    background: #fff;
    border-radius: 20px;
    box-shadow: 0 2px 14px rgba(0,0,0,0.08);
    padding: 20px;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const MiTerapeuta = ({ toggleMenu }) => {
    const history = useHistory();
    const [user] = useUser();
    const [therapists, therapistsDispatcher] = useTherapist();
    const [appointments, appointmentsDispatcher] = useAppointments();
    const [categories, categoriesDispatcher] = useCategories();

    const assignedTherapist = user.current?.extraData?.therapist;
    const therapistId = assignedTherapist?.id;

    useEffect(() => {
        if (therapistId) {
            therapistsDispatcher.fetchProfileStart(therapistId);
        }
        appointmentsDispatcher.fetchStart();
        if (!categories.list?.length) {
            categoriesDispatcher.fetchStart();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [therapistId]);

    // Historial de citas con este terapeuta
    const appointmentHistory = useMemo(() => {
        if (!appointments.list || !therapistId) return [];
        return [...appointments.list]
            .filter((a) => Number(a.therapistId) === Number(therapistId))
            .sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [appointments.list, therapistId]);

    // Rating y reseñas
    const validReviews = useMemo(() => {
        const raw = therapists.current?.reviews;
        if (!Array.isArray(raw)) return [];
        return raw.filter((r) => r?.id != null && r?.rating != null);
    }, [therapists.current?.reviews]);

    const reviewAvg = useMemo(() => {
        if (!validReviews.length) return null;
        const avg = validReviews.reduce((sum, r) => sum + Number(r.rating), 0) / validReviews.length;
        return Number(avg.toFixed(2));
    }, [validReviews]);

    // Especialidades: IDs → nombres
    const specialtyNames = useMemo(() => {
        const ids = therapists.current?.categories;
        if (!Array.isArray(ids) || !categories.list?.length) return [];
        return ids
            .map((id) => categories.list.find((c) => c.id === id)?.name)
            .filter(Boolean);
    }, [therapists.current?.categories, categories.list]);

    // Disponibilidad: días únicos con slots (timeAvailability = { uuid: ISODateString })
    const availableDays = useMemo(() => {
        const ta = therapists.current?.timeAvailability;
        if (!ta) return [];
        const uniqueIndices = new Set(
            Object.values(ta).map((iso) => new Date(iso).getDay())
        );
        return [...uniqueIndices]
            .sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b)) // lun→dom
            .map((idx) => DAY_NAMES[idx]);
    }, [therapists.current?.timeAvailability]);

    const handleBookAppointment = () => {
        history.push(`/terapeutas/${therapistId}`);
    };

    const handleSendMessage = () => {
        history.push('/home/mensajes');
    };

    const isLoading = therapists.fetching?.state;
    const profile = therapists.current;
    const hasProfile = !isLoading && profile?.id === Number(therapistId);

    if (!therapistId) {
        return (
            <PageWrap>
                <SubpageTopBar title="Mi terapeuta" toggleMenu={toggleMenu} />
                <Content>
                    <EmptyHistoryText>No tienes un terapeuta asignado aún.</EmptyHistoryText>
                </Content>
            </PageWrap>
        );
    }

    return (
        <PageWrap>
            <SubpageTopBar title="Mi terapeuta" toggleMenu={toggleMenu} />
            <Content>
                {/* --- Profile card --- */}
                {isLoading || !hasProfile ? (
                    <SkeletonCard>
                        <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
                            <SkeletonBlock w="72px" h="72px" style={{ borderRadius: '50%' }} />
                            <div style={{ flex: 1 }}>
                                <SkeletonBlock h="18px" w="60%" mb="8px" />
                                <SkeletonBlock h="12px" w="40%" />
                            </div>
                        </div>
                        <SkeletonBlock h="14px" mb="6px" />
                        <SkeletonBlock h="14px" w="80%" />
                    </SkeletonCard>
                ) : (
                    <ProfileCard>
                        <ProfileHeader>
                            <Avatar>
                                <img
                                    src={profile.profileImg ? `${IMAGES_URL}${profile.profileImg}` : NoProfileSVG}
                                    alt={`${profile.name} ${profile.lastName}`}
                                />
                            </Avatar>
                            <ProfileInfo>
                                <TherapistName>
                                    {profile.title ? `${profile.title} ` : ''}{profile.name} {profile.lastName}
                                </TherapistName>
                                {profile.experience && (
                                    <TherapistTitle>{profile.experience}</TherapistTitle>
                                )}
                            </ProfileInfo>
                        </ProfileHeader>

                        {profile.phrase && <Phrase>"{profile.phrase}"</Phrase>}

                        {/* Rating */}
                        <RatingRow>
                            <RatingStars
                                reviewAvg={reviewAvg}
                                reviewsCount={validReviews.length}
                            />
                        </RatingRow>

                        {profile.about && (
                            <div>
                                <SectionLabel>Acerca de</SectionLabel>
                                <AboutText>{profile.about}</AboutText>
                            </div>
                        )}
                    </ProfileCard>
                )}

                {/* --- CTAs --- */}
                <CtaRow>
                    <BookButton onClick={handleBookAppointment}>
                        📅 Agendar cita
                    </BookButton>
                    <MessageButton onClick={handleSendMessage}>
                        💬 Mensaje
                    </MessageButton>
                </CtaRow>

                {/* --- Especialidades --- */}
                {hasProfile && specialtyNames.length > 0 && (
                    <SpecialtiesCard>
                        <SectionLabel>Especialidades</SectionLabel>
                        <ChipRow>
                            {specialtyNames.map((name) => (
                                <SpecialtyChip key={name}>{name}</SpecialtyChip>
                            ))}
                        </ChipRow>
                    </SpecialtiesCard>
                )}

                {/* --- Disponibilidad --- */}
                {hasProfile && availableDays.length > 0 && (
                    <AvailabilityCard>
                        <SectionLabel>Disponibilidad</SectionLabel>
                        <DayGrid>
                            {availableDays.map((day) => (
                                <DayChip key={day}>{day}</DayChip>
                            ))}
                        </DayGrid>
                    </AvailabilityCard>
                )}

                {/* --- Reseñas --- */}
                {hasProfile && validReviews.length > 0 && (
                    <ReviewsCard>
                        <SectionLabel>Reseñas de pacientes ({validReviews.length})</SectionLabel>
                        {validReviews.slice(0, 5).map((review) => (
                            <ReviewItem key={review.id}>
                                <ReviewStarRow>
                                    {[1,2,3,4,5].map((s) => (
                                        <StarMini key={s} filled={s <= review.rating}>★</StarMini>
                                    ))}
                                </ReviewStarRow>
                                <ReviewAuthor>{review.authorName} {review.authorLastName}</ReviewAuthor>
                                {review.comment && <ReviewComment>"{review.comment}"</ReviewComment>}
                            </ReviewItem>
                        ))}
                    </ReviewsCard>
                )}

                {/* --- Historial de citas --- */}
                <HistoryCard>
                    <SectionLabel>Historial de citas</SectionLabel>
                    {appointments.fetching?.state ? (
                        <>
                            <SkeletonBlock h="72px" mb="8px" style={{ borderRadius: 14, marginTop: 8 }} />
                            <SkeletonBlock h="72px" mb="8px" style={{ borderRadius: 14 }} />
                        </>
                    ) : appointmentHistory.length === 0 ? (
                        <EmptyHistoryText>Aún no tienes citas con este terapeuta.</EmptyHistoryText>
                    ) : (
                        appointmentHistory.map((appt) => (
                            <AppointmentCard key={appt.id} app={appt} isTherapist={false} />
                        ))
                    )}
                </HistoryCard>
            </Content>
        </PageWrap>
    );
};

export default MiTerapeuta;
